import axios from "axios";
import { useDrupalSettings } from "@/composables/useDrupalSettings";

const { config } = useDrupalSettings();

export default class ElasticsearchClient {
  /**
   * Instance of the Elasticsearch Auth Manager.
   *
   * @type {null|ElasticsearchAuthManager}
   */
  authManager = null;

  /**
   * List of fields to source fields to return.
   *
   * @type {string[]}
   */
  sourceFields = [
    "text",
    "title",
    "field_chemdb_physical_state",
    "uuid",
    "field_chemdb_cas_number",
  ];

  /**
   * ChemDb Elasticsearch client.
   *
   * @param {ElasticsearchAuthManager} authManager - Bearer token for auth.
   *
   * @constructor
   */
  constructor(authManager) {
    this.authManager = authManager;
    this.indices = {
      chemicals: config.chemicalSearchIndexes.chemicals,
      products: config.chemicalSearchIndexes.products,
    };
  }

  /**
   * Create a new Axios request that presets the baseUrl and auth header.
   */
  createRestRequest() {
    this.restRequest = axios.create({
      baseURL: config.chemicalSearchHost,
      headers: {
        Authorization: "Bearer " + this.authManager.getAuthToken(),
      },
    });
  }

  /**
   * Elasticsearch ping.
   */
  async ping() {
    this.createRestRequest();
    const response = await this.restRequest.get().catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    return response;
  }

  /**
   * Performs autocomplete searches against Elasticsearch.
   *
   * @param {String} searchType
   *   The string value from the chemical search widget radio buttons.
   * @param {String} searchString
   *   The string value from the chemical search widget.
   */
  async search(searchType, searchString) {
    this.createRestRequest();
    searchType = typeof searchType !== "undefined" ? searchType : "suggest";
    let body = {
      _source: this.sourceFields,
    };
    let indexName = this.indices.chemicals;
    if (searchType === "suggest") {
      const suggestSearchBody = this.buildSuggestSearchBody(searchString);
      body = { ...body, ...suggestSearchBody };
    }
    if (searchType === "cas_number") {
      const casSearchBody = this.buildCasNumberSearchBody(searchString);
      body = { ...body, ...casSearchBody };
    }
    if (searchType === "product") {
      // Return all fields, no filtering.
      body = this.buildProductSearchBody(searchString);
      indexName = this.indices.products;
    }

    const response = await this.restRequest
      .post("/" + indexName + "/_search", body, {
        headers: { "Content-Type": "application/json" },
      })
      .catch((error) => {
        /* eslint-disable no-console */
        console.error(error);
        return Promise.reject(error);
      });
    return response.data;
  }

  /**
   * Find a chemical by UUID.
   *
   * @param {String} uuid - String representing a chemical entity uuid.
   */
  async findChemicalByUUID(uuid) {
    this.createRestRequest();
    const body = {
      _source: this.sourceFields,
      query: {
        term: {
          uuid: uuid,
        },
      },
    };
    const indexName = this.indices.chemicals;
    try {
      const response = await this.restRequest.post(
        "/" + indexName + "/_search",
        body,
      );
      return response.data;
    } catch (error) {
      /* eslint-disable no-console */
      console.dir(error);
      return Promise.reject(error);
    }
  }

  /**
   * Build the body object for chemical autocomplete searches.
   *
   * @param {String} searchString - String representing the search entered by a user.
   *
   * @return {Object} Returns an object representing the suggest search body.
   */
  buildSuggestSearchBody(searchString) {
    return {
      suggest: {
        chemical_suggest: {
          prefix: searchString,
          completion: {
            field: "chemical_synonym_suggest",
            size: 20,
          },
        },
      },
    };
  }

  /**
   * Build the body object for chemical cas number searches.
   *
   * @param {String} searchString - String representing the search entered by a user.
   *
   * @return {Object} Returns an object representing the cas number search body.
   */
  buildCasNumberSearchBody(searchString) {
    return {
      query: {
        term: {
          field_chemdb_cas_number: searchString,
        },
      },
    };
  }

  /**
   * Build the body object for the product searches.
   *
   * @param {String} searchString - String representing the search entered by a user.
   *
   * @return {Object} Returns an object representing the product search body.e
   */
  buildProductSearchBody(searchString) {
    return {
      size: 25,
      query: {
        bool: {
          should: [
            {
              match: {
                product_name: {
                  query: searchString,
                  operator: "or",
                  fuzziness: 0,
                },
              },
            },
            {
              term: {
                product_number: searchString.toLowerCase(),
              },
            },
          ],
        },
      },
    };
  }
}

export { ElasticsearchClient };
